import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Star } from 'lucide-react';

const TrustedMarquee = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });

  // Particles inside the marquee (they will be positioned absolutely inside the container)
  const innerParticles = Array.from({ length: 24 }, (_, i) => ({
    id: i,
    size: Math.random() * 4 + 2,          // 2-6px
    top: Math.random() * 100,             // 0-100% height
    left: Math.random() * 100,            // 0-100% width
    delay: Math.random() * 5,
    duration: Math.random() * 6 + 4,
    opacity: Math.random() * 0.4 + 0.2,
  }));

  const text = "One of the Most Trusted Game Development Studios";

  return (
    <section
      ref={sectionRef}
      className="relative w-full bg-gradient-to-br from-white via-gray-50 to-white py-12 overflow-hidden"
    >
      {/* Background orbs and particles (subtle, optional) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-red-200/30 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-rose-200/30 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-red-100/20 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4">
        <div className="group relative">
          {/* Hover glow effect */}
          <div className="absolute -inset-0.5 bg-gradient-to-r from-red-500/0 via-red-500/50 to-rose-500/0 rounded-full opacity-0 group-hover:opacity-100 blur-lg transition-opacity duration-500" />
          
          <div className="relative bg-white/60 backdrop-blur-md border border-white/20 rounded-full overflow-hidden transition-all duration-300 group-hover:border-red-300/50">
            {/* Light sweep effect (optional) */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-full">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full animate-sweep" />
            </div>

            {/* Inner floating particles */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-full">
              {innerParticles.map((p) => (
                <div
                  key={p.id}
                  className="absolute rounded-full bg-red-400/60"
                  style={{
                    width: `${p.size}px`,
                    height: `${p.size}px`,
                    top: `${p.top}%`,
                    left: `${p.left}%`,
                    opacity: p.opacity,
                    animation: `floatParticle ${p.duration}s ease-in-out infinite`,
                    animationDelay: `${p.delay}s`,
                  }}
                />
              ))}
            </div>

            <div className="marquee-container overflow-hidden whitespace-nowrap py-4 md:py-5">
              <div className="marquee-content inline-flex items-center gap-3 text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold animate-marquee">
                {/* First copy */}
                <span className="inline-flex items-center gap-3 group-hover:drop-shadow-lg transition-all duration-300">
                  <Star className="w-5 h-5 text-red-500 fill-red-500/20 group-hover:drop-shadow-[0_0_4px_rgba(239,68,68,0.6)] transition-all" />
                  <span className="bg-gradient-to-r from-red-600 via-red-500 to-rose-500 bg-clip-text text-transparent group-hover:drop-shadow-[0_0_3px_rgba(239,68,68,0.4)] transition-all">
                    {text}
                  </span>
                  <Star className="w-5 h-5 text-red-500 fill-red-500/20 group-hover:drop-shadow-[0_0_4px_rgba(239,68,68,0.6)] transition-all" />
                </span>
                {/* Duplicate copy */}
                <span className="inline-flex items-center gap-3 group-hover:drop-shadow-lg transition-all duration-300">
                  <Star className="w-5 h-5 text-red-500 fill-red-500/20 group-hover:drop-shadow-[0_0_4px_rgba(239,68,68,0.6)] transition-all" />
                  <span className="bg-gradient-to-r from-red-600 via-red-500 to-rose-500 bg-clip-text text-transparent group-hover:drop-shadow-[0_0_3px_rgba(239,68,68,0.4)] transition-all">
                    {text}
                  </span>
                  <Star className="w-5 h-5 text-red-500 fill-red-500/20 group-hover:drop-shadow-[0_0_4px_rgba(239,68,68,0.6)] transition-all" />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .marquee-container {
          overflow: hidden;
          white-space: nowrap;
        }
        .marquee-content {
          display: inline-flex;
          will-change: transform;
          transform: translate3d(0, 0, 0);
          backface-visibility: hidden;
          perspective: 1000px;
        }
        @keyframes marquee {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        .animate-marquee {
          animation: marquee 24s linear infinite;
        }
        .group:hover .animate-marquee {
          animation-play-state: paused;
        }
        @keyframes sweep {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        .animate-sweep {
          animation: sweep 5s ease-in-out infinite;
        }
        @keyframes floatParticle {
          0%, 100% {
            transform: translateY(0px) translateX(0px);
          }
          50% {
            transform: translateY(-6px) translateX(4px);
          }
        }
      `}</style>
    </section>
  );
};

export default TrustedMarquee;